<template>
  <v-dialog :value="value" max-width="680" @input="$emit('input', $event)">
    <v-card v-if="current" class="report-dialog">
      <v-card-title class="report-dialog-heading">
        <div><span>{{ current.title }}</span><div class="report-id">{{ current.id }}</div></div>
        <v-spacer />
        <v-btn icon aria-label="Закрыть отчёт" title="Закрыть" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="error" dense text type="error" class="mb-4">{{ error }}<v-btn icon small title="Повторить обновление" aria-label="Повторить обновление" @click="refresh"><v-icon small>mdi-refresh</v-icon></v-btn></v-alert>
        <div class="report-state-line">
          <span><v-icon small :color="state.color" class="mr-1">{{ state.icon }}</v-icon>{{ state.label }}</span>
          <strong>{{ current.progress || 0 }}%</strong>
        </div>
        <v-progress-linear :value="current.progress || 0" :color="current.status === 'failed' ? 'error' : current.status === 'ready' ? 'success' : 'primary'" height="8" class="my-3" />
        <div class="report-phase">{{ phase }}</div>
        <div v-if="current.total_rows !== null" class="report-count">{{ number(current.processed_rows) }} / {{ number(current.total_rows) }} строк</div>
        <v-alert v-if="current.status === 'failed'" dense text type="error" class="mt-4">{{ current.error_message || 'Не удалось сформировать отчёт.' }}</v-alert>
        <v-alert v-if="current.status === 'completed'" dense text type="info" class="mt-4">{{ current.file_deleted_at ? 'Срок хранения истёк. Файл удалён, параметры отчёта сохранены.' : 'Срок хранения истёк. Файл больше недоступен.' }}</v-alert>
        <dl class="report-metadata">
          <div><dt>Создал</dt><dd>{{ current.requested_by }}</dd></div>
          <div><dt>Запрошен</dt><dd>{{ date(current.created_at) }}</dd></div>
          <div v-if="current.started_at"><dt>Начат</dt><dd>{{ date(current.started_at) }}</dd></div>
          <div v-if="current.finished_at"><dt>Сформирован</dt><dd>{{ date(current.finished_at) }}</dd></div>
          <div v-if="current.expires_at"><dt>Файл доступен до</dt><dd>{{ date(current.expires_at) }}</dd></div>
          <div v-if="current.file_size"><dt>Размер</dt><dd>{{ size(current.file_size) }}</dd></div>
        </dl>
        <h3 class="report-parameters-title">Параметры</h3>
        <dl class="report-parameters"><div v-for="filter in current.filters" :key="filter.label"><dt>{{ filter.label }}</dt><dd>{{ filter.value }}</dd></div></dl>
        <div v-if="current.status === 'ready'" class="report-filename"><v-icon small class="mr-1">mdi-file-excel-outline</v-icon>{{ current.filename }}</div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-6 py-4 report-dialog-actions">
        <v-btn text :to="{name: 'reports-index', query: {report: current.id}}" @click="$emit('input', false)">Все отчёты</v-btn>
        <v-spacer />
        <v-btn v-if="current.status === 'failed' || current.status === 'completed'" outlined :loading="recreating" @click="repeat"><v-icon left small>mdi-replay</v-icon>Сформировать снова</v-btn>
        <v-btn v-if="current.can_download" class="btn-primary" :loading="downloading" :disabled="downloading" @click="download"><v-icon left small>mdi-download</v-icon>Скачать XLSX</v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else><v-card-text class="pa-6">Загрузка отчёта…</v-card-text><v-card-actions><v-btn text @click="$emit('input', false)">Закрыть</v-btn></v-card-actions></v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import ReportService, {POLL_INTERVAL, reportError, reportStates, requestKey} from '../../api/ReportService'

export default {
  name: 'ReportProgressDialog',
  props: {value: Boolean, report: {type: Object, default: null}},
  data: () => ({current: null, error: '', timer: null, busy: false, generation: 0, destroyed: false, downloading: false, recreating: false}),
  computed: {
    state() { return reportStates[this.current && this.current.status] || reportStates.queued },
    phase() {
      return ({queued: 'Ожидает обработки', counting: 'Подсчёт клиентов', writing: 'Запись строк в файл', finalizing: 'Подготовка файла к скачиванию', ready: 'Файл сформирован', failed: 'Формирование остановлено', completed: 'Хранение завершено'})[this.current && this.current.phase] || ''
    },
  },
  watch: {
    value(open) { this.generation++; this.stop(); if (open) { this.current = this.report; this.error = ''; this.refresh() } },
    report(report) { if (report && (!this.current || report.id !== this.current.id)) { this.generation++; this.current = report; this.stop(); if (this.value) this.refresh() } },
  },
  mounted() { document.addEventListener('visibilitychange', this.visibility); if (this.value) { this.current = this.report; this.refresh() } },
  beforeDestroy() { this.destroyed = true; this.generation++; this.stop(); document.removeEventListener('visibilitychange', this.visibility) },
  methods: {
    date(value) { return value ? moment(value).format('DD.MM.YYYY HH:mm') : '—' },
    number(value) { return Number(value || 0).toLocaleString('ru-RU') },
    size(value) { return value >= 1048576 ? (value / 1048576).toFixed(1) + ' МБ' : Math.max(1, Math.ceil(value / 1024)) + ' КБ' },
    stop() { clearTimeout(this.timer); this.timer = null },
    visibility() { if (document.hidden) this.stop(); else if (this.value) this.refresh() },
    async refresh() {
      this.stop()
      if (this.busy || !this.value || !this.current || this.destroyed) return
      this.busy = true
      const generation = this.generation
      const id = this.current.id
      try {
        const response = await ReportService.get(id)
        if (generation === this.generation && this.value && !this.destroyed) { this.current = response.data; this.error = ''; this.$emit('updated', response.data) }
      } catch (error) {
        if (generation === this.generation && this.value && !this.destroyed) this.error = await reportError(error, 'Не удалось обновить статус отчёта.')
      } finally {
        this.busy = false
        if (!this.destroyed && this.value && !document.hidden && (this.error || ['queued', 'processing'].includes(this.current && this.current.status))) this.timer = setTimeout(this.refresh, generation === this.generation ? POLL_INTERVAL : 0)
      }
    },
    async download() {
      if (this.downloading) return
      this.downloading = true; this.error = ''
      try { await ReportService.download(this.current) }
      catch (error) { this.error = await reportError(error, 'Не удалось скачать отчёт. Попробуйте ещё раз.'); this.refresh() }
      finally { this.downloading = false }
    },
    async repeat() {
      if (this.recreating) return
      this.recreating = true; this.error = ''
      try {
        const response = await ReportService.create({...this.current.request, request_key: requestKey()})
        this.generation++; this.current = response.data; this.$emit('created', response.data); this.refresh()
      } catch (error) { this.error = await reportError(error) }
      finally { this.recreating = false }
    },
  },
}
</script>

<style scoped>
.report-dialog-heading { align-items: flex-start; padding: 22px 24px 12px; font-size: 22px; line-height: 1.35; word-break: break-word; }
.report-id { color: #7a8490; font-size: 12px; font-weight: 400; margin-top: 4px; overflow-wrap: anywhere; }
.report-state-line { display: flex; justify-content: space-between; align-items: center; gap: 12px; color: #263441; }
.report-phase { color: #354351; font-size: 14px; }
.report-count { color: #74808c; margin-top: 3px; font-size: 13px; }
.report-metadata { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 24px; margin: 24px 0; }
.report-metadata dt, .report-parameters dt { color: #74808c; font-size: 12px; font-weight: 400; }
.report-metadata dd, .report-parameters dd { color: #263441; margin: 2px 0 0; font-size: 14px; overflow-wrap: anywhere; }
.report-parameters-title { font-size: 15px; font-weight: 600; color: #263441; margin: 16px 0 10px; }
.report-parameters > div { display: grid; grid-template-columns: 170px minmax(0, 1fr); gap: 12px; padding: 6px 0; border-bottom: 1px solid #eef1f4; }
.report-filename { margin-top: 18px; overflow-wrap: anywhere; }
.report-dialog-actions { flex-wrap: wrap; gap: 8px; }
@media (max-width: 500px) { .report-metadata { gap: 12px; } .report-parameters > div { grid-template-columns: 1fr; gap: 0; } .report-dialog-actions .spacer { display: none; } .report-dialog-heading { font-size: 20px; } }
</style>
